// Surfaces a small banner when a new version of the PWA has been downloaded
// in the background. Operators choose Reload (now) or Later.
//
// Backed by vite-plugin-pwa's useRegisterSW hook. This REQUIRES
// registerType: 'prompt' in vite.config.ts — under 'autoUpdate' the plugin
// force-enables skipWaiting/clientsClaim and never wires onNeedRefresh, so
// this entire component was dead code from 2026-05-07 to 2026-06-11 while
// deploys silently hot-swapped the SW under live pages. In prompt mode the
// new SW installs and WAITS; needRefresh fires; Reload sends SKIP_WAITING
// and refreshes the page in one motion. scripts/assert-sw-mode.mjs guards
// the built sw.js against regressing to auto mode.

import { useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { Download, X, CheckCircle2 } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { FLOOR_MONITOR_PATH } from '@/lib/unattendedDisplayPaths'

// Five-minute background poll while the tab stays open. Combined with the
// visibilitychange + online listeners below, this means a Vercel deploy
// surfaces to an open tab within five minutes worst case, or instantly the
// next time the user comes back to the tab / reconnects.
const CHECK_INTERVAL_MS = 5 * 60 * 1000

export default function UpdateBanner() {
  const { pathname } = useLocation()
  // Nobody reads the floor TV: the card would sit over the board until someone
  // reloads it. Polling continues; only the card is withheld (U24 R2a: /monitor only,
  // not the clock kiosk).
  const unattended = pathname === FLOOR_MONITOR_PATH
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      const check = () => registration.update().catch(() => {})

      // Periodic background poll.
      setInterval(check, CHECK_INTERVAL_MS)

      // Re-check the moment the user comes back to the tab (a tab idle
      // for hours otherwise sits on the old version until the next
      // 5-minute tick).
      const onVisible = () => { if (document.visibilityState === 'visible') check() }
      document.addEventListener('visibilitychange', onVisible)

      // And the moment the network comes back — covers the packhouse
      // Wi-Fi-blip case where the SW couldn't fetch updates while
      // offline.
      window.addEventListener('online', check)
    },
  })

  // Auto-dismiss the prompt when the user navigates — staying on the old
  // (internally consistent) bundle is safe in prompt mode, and the banner
  // re-fires on the next app boot while a SW is still waiting.
  useEffect(() => {
    if (!needRefresh) return
    const onPop = () => setNeedRefresh(false)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [needRefresh, setNeedRefresh])

  // Auto-dismiss the "offline ready" toast after a few seconds — it's a
  // confirmation, not an action the user has to take.
  useEffect(() => {
    if (!offlineReady) return
    const t = setTimeout(() => setOfflineReady(false), 6000)
    return () => clearTimeout(t)
  }, [offlineReady, setOfflineReady])

  // Toggle a body class while either banner is showing so the floating FAB
  // (App.tsx) can shift up out of the collision zone. CSS in index.css
  // does the actual position bump — keeps the visual offset declarative
  // instead of plumbing state across components.
  useEffect(() => {
    if ((needRefresh && !unattended) || offlineReady) document.body.classList.add('update-banner-visible')
    else                            document.body.classList.remove('update-banner-visible')
    return () => document.body.classList.remove('update-banner-visible')
  }, [needRefresh, offlineReady, unattended])

  // "Saved for offline use" — fires once the service worker has finished
  // precaching the whole app (first install, or after a ?reset-sw=1 / cache
  // reset). This is the signal field users have been missing: it tells them the
  // device is now fully cached, so it's SAFE to go offline. Going offline before
  // this shows is exactly what left modules uncached ("Scouting works, next
  // module → blank"). Shown only when there isn't a more important update prompt.
  if (offlineReady && !needRefresh) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-chrome max-w-md w-[calc(100%-2rem)]
                      bg-emerald-600 text-white rounded-2xl shadow-2xl px-4 py-3
                      flex items-center gap-3 animate-in slide-in-from-bottom-4 print:hidden">
        <CheckCircle2 size={18} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Saved for offline use</p>
          <p className="text-xs opacity-90">The whole app is cached — you can work without signal now.</p>
        </div>
        <button
          onClick={() => setOfflineReady(false)}
          className="text-white/80 hover:text-white shrink-0"
          aria-label="Dismiss — keep using this version">
          <X size={16} />
        </button>
      </div>
    )
  }

  if (!needRefresh || unattended) return null

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-chrome max-w-md w-[calc(100%-2rem)]
                    bg-emerald-600 text-white rounded-2xl shadow-2xl px-4 py-3
                    flex items-center gap-3 animate-in slide-in-from-bottom-4 print:hidden">
      <Download size={18} className="shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">New version ready</p>
        <p className="text-xs opacity-90">Reload to apply the latest changes.</p>
      </div>
      <button
        onClick={async () => {
          // updateServiceWorker(true) does the right thing in theory —
          // sends SKIP_WAITING to the new SW, then listens for the
          // browser's controllerchange event to reload. In practice the
          // controllerchange event can race with the page's SW state on
          // some browsers (operator report 2026-05-20: "the banner says
          // updating but doesn't, Ctrl+R works"), and the reload that
          // vite-plugin-pwa is supposed to fire never arrives.
          //
          // Belt-and-braces: trigger the SW skip-waiting AND fall back to
          // an explicit window.location.reload after a short grace period
          // so the new SW has time to activate before the reload nav
          // tries to fetch through it.
          try { await updateServiceWorker(true) } catch { /* fall through */ }
          setTimeout(() => window.location.reload(), 500)
        }}
        className="tap-target-y bg-white text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-1.5 text-sm font-semibold shrink-0">
        Reload
      </button>
      <button
        onClick={() => setNeedRefresh(false)}
        className="text-white/80 hover:text-white shrink-0"
        aria-label="Dismiss — keep using this version">
        <X size={16} />
      </button>
    </div>
  )
}
